import { SalesChartPeriod } from '@/types/other';
import { Orders, UpdateData, MarketValueData } from '@/types/salesChart';
import { useEffect, useState } from 'react';

const useChartData = (
  period: SalesChartPeriod,
  orders: Orders[],
  marketValuesResult: MarketValueData,
  skuRetailPrice: number,
  startDate: Date
) => {
  const [data, setData] = useState<UpdateData[]>([]);
  const [monthData, setMonthData] = useState<UpdateData[]>([]);
  const [weekData, setWeekData] = useState<UpdateData[]>([]);
  const [sixMonthData, setSixMonthData] = useState<UpdateData[]>([]);
  const [yearData, setYearData] = useState<UpdateData[]>([]);

  useEffect(() => {
    function doChartData() {
      const updatedData: UpdateData[] = [];
      const updatedmonthlyData: UpdateData[] = [];
      let date = new Date(startDate);
      date.setDate(date.getDate() + 1);
      const today = new Date();

      // HashMaps zur schnellen Zuordnung der Markt- und Verkaufswerte nach Monat
      const salesByMonth = new Map<string, number[]>();
      const marketValuesByMonth = new Map<string, number[]>();

      orders.forEach((order) => {
        const paidDate = new Date(order.skuOrderStatus.paid);
        const monthStr = new Intl.DateTimeFormat('en-US', {
          year: '2-digit',
          month: 'short',
        }).format(paidDate);
        if (!salesByMonth.has(monthStr)) {
          salesByMonth.set(monthStr, []);
        }
        salesByMonth.get(monthStr)!.push(order.payValue);
      });

      marketValuesResult.forEach((marketValue) => {
        const createdDate = new Date(marketValue.created);
        const monthStr = new Intl.DateTimeFormat('en-US', {
          year: '2-digit',
          month: 'short',
        }).format(createdDate);
        if (!marketValuesByMonth.has(monthStr)) {
          marketValuesByMonth.set(monthStr, []);
        }
        marketValuesByMonth.get(monthStr)!.push(marketValue.value);
      });

      //Sales Fill Data & Sort
      let mv = 0;
      let mv_org = 0;
      let sv: number | null = 0;
      let mv_value: number | null = 0;
      let mv_value_lock = true;

      const monthlydays = getLastNDays(31);

      if (orders.length > 35) {
        date = new Date(orders[35].skuOrderStatus.paid);
      }

      // Make Month array
      while (date <= today) {
        const monthStr = new Intl.DateTimeFormat('en-US', {
          year: '2-digit',
          month: 'short',
        }).format(date);

        // Check Sales Date
        const sales_arr: number[] = [];

        for (let i = 0; i < Math.min(orders.length, 35); i++) {
          const paid_date = new Date(orders[i].skuOrderStatus.paid);
          const monthStr_arr = new Intl.DateTimeFormat('en-US', {
            year: '2-digit',
            month: 'short',
          }).format(paid_date);

          if (monthStr === monthStr_arr) {
            sales_arr.push(orders[i].payValue);
          }

          // Check Monthly
          if (monthStr === monthStr_arr) {
            let date_monhtly_check: Date | string = paid_date;
            date_monhtly_check.setDate(date_monhtly_check.getDate() + 1);
            date_monhtly_check.setHours(0, 0, 0, 0); // Uhrzeit auf 00:00:00 setzen
            date_monhtly_check = date_monhtly_check.toISOString().split('T')[0];

            // Get Nearest Market Value on oldest date for monthly check
            const oldestmonthDate = new Date(
              Math.min(
                ...monthlydays.map((day) => new Date(day.name).getTime())
              )
            );

            const neardate = marketValuesResult.reduce(
              (
                a: { created: string; value: number | null },
                b: { created: string; value: number | null }
              ) =>
                Math.abs(
                  new Date(b.created).getTime() - oldestmonthDate.getTime()
                ) <
                Math.abs(
                  new Date(a.created).getTime() - oldestmonthDate.getTime()
                )
                  ? b
                  : a,
              { created: new Date(0).toISOString(), value: null }
            ).value;
            if (mv_value_lock) {
              mv_value = neardate;
              mv_value_lock = false;
            }

            // Check for monthly MV Changes
            for (let j = 1; j < monthlydays.length; j++) {
              // Check Market Value change
              for (let mv_c = 0; mv_c < marketValuesResult.length; mv_c++) {
                if (
                  new Date(monthlydays[j].name).toDateString() ===
                  new Date(marketValuesResult[mv_c].created).toDateString()
                ) {
                  mv_value = marketValuesResult[mv_c].value;
                }
              }

              const existingEntry: UpdateData | undefined =
                updatedmonthlyData.find(
                  (entry) => entry.name === formatDate(monthlydays[j].name)
                );
              if (
                existingEntry &&
                new Date(monthlydays[j].name).toDateString() ===
                  new Date(orders[i].skuOrderStatus.paid).toDateString()
              ) {
                (existingEntry.sales = existingEntry.sales || []).push(
                  orders[i].payValue
                );
                existingEntry.sv = Math.max(
                  existingEntry.sv || 0,
                  orders[i].payValue
                );
              } else if (
                new Date(monthlydays[j].name).toDateString() ===
                new Date(orders[i].skuOrderStatus.paid).toDateString()
              ) {
                updatedmonthlyData.push({
                  name: formatDate(monthlydays[j].name),
                  sales: [orders[i].payValue],
                  sv: orders[i].payValue,
                  mv: mv_value,
                });
              } else if (!existingEntry) {
                updatedmonthlyData.push({
                  name: formatDate(monthlydays[j].name),
                  sales: [],
                  sv: null,
                  mv: mv_value,
                });
              }
            }
          }
        }

        // Check MarketValue Date
        const mv_arr = [];
        for (let i = 0; i < marketValuesResult.length; i++) {
          const monthStr_arr = new Intl.DateTimeFormat('en-US', {
            year: '2-digit',
            month: 'short',
          }).format(new Date(marketValuesResult[i].created));
          if (monthStr === monthStr_arr) {
            mv_arr.push(marketValuesResult[i].value);
          }
        }

        // Sort
        if (mv_arr.length > 0) {
          mv_org = Math.max(...mv_arr);
          mv = Math.max(...mv_arr);
        }

        if (sales_arr.length > 0) {
          sv = Math.max(...sales_arr);
        } else {
          sv = null;
        }

        if (sales_arr.length === 0 && mv_arr.length === 0) {
          mv = mv_org;
        }

        // Push Data
        updatedData.push({
          name: monthStr,
          sales: sales_arr,
          sv: sv,
          mv: mv,
        });
        date.setMonth(date.getMonth() + 1); // Wechsel zum nächsten Monat
      }

      // Sort by Date
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      function parseDateString(dateString: string) {
        const [day, month] = dateString.split(' ');
        const monthIndex = monthNames.indexOf(month);
        return new Date(2023, monthIndex, parseInt(day)); // Verwenden Sie das Jahr 2023 als Platzhalter
      }

      updatedmonthlyData.sort((a, b) => {
        const dateA = parseDateString(a.name).getTime();
        const dateB = parseDateString(b.name).getTime();
        return dateA - dateB;
      });

      setData(updatedData);
      setMonthData(updatedmonthlyData);
      setWeekData(updatedmonthlyData.slice(-7));
      setSixMonthData(updatedData.slice(-7));
      setYearData(updatedData.slice(-13));
    }
    doChartData();
  }, [startDate]);

  return period === 'Week'
    ? weekData
    : period === 'Month'
      ? monthData
      : period === '6M'
        ? sixMonthData
        : period === '1Y'
          ? yearData
          : data;
};

export default useChartData;

const getLastNDays = (n: number) => {
  const dates: UpdateData[] = [];
  const today = new Date(
    Date.UTC(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    )
  );

  for (let i = 0; i < n; i++) {
    const date = new Date(today);
    date.setUTCDate(today.getUTCDate() - i);
    dates.push({
      name: date.toISOString().split('T')[0],
      mv: 0,
      sv: 0,
      sales: null,
    });
  }

  dates.reverse();
  return dates;
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  return `${day} ${month}`;
}
